/* One consented six-week CTA-copy test. Session assignment only; no personal identifier.
 * An exposure is saved after the event bridge accepts its handoff, not as proof of GA4 receipt.
 */
(() => {
  'use strict';
  if (window.CgauCommerceExperiment) return;
  const KEY = 'cgau-setup-entry-v1', EXPERIMENT = 'setup_entry_v1';
  const START = Date.parse('2026-09-07T00:00:00Z'), END = Date.parse('2026-10-19T00:00:00Z');
  const SELECTOR = 'a[data-cgau-setup-entry]';
  const originals = new WeakMap(), targets = new Set();
  let cohort = null, observer = null, mutations = null, generation = 0;
  let stopped = false, suspended = false, retryTimer, expiryTimer, attempts = 0;

  const inWindow = () => Date.now() >= START && Date.now() < END;
  const qa = () => window.Shopify?.designMode === true || ['unpublished', 'development'].includes(window.Shopify?.theme?.role) || document.documentElement.dataset.cgauQa === 'true' || ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname);
  const consent = () => {
    try { return window.CgauCommerceEvents?.allowed() === true; } catch (_) { return false; }
  };
  const active = () => !stopped && !suspended && !document.hidden && inWindow() && !qa() && consent();
  const transportReady = () => typeof window.Shopify?.analytics?.publish === 'function' && typeof window.CgauCommerceEvents?.status === 'function';
  function save() {
    try { sessionStorage.setItem(KEY, JSON.stringify(cohort)); return true; } catch (_) { return false; }
  }
  function removeSaved() { try { sessionStorage.removeItem(KEY); } catch (_) {} }
  function restore() {
    for (const link of targets) if (originals.has(link)) link.textContent = originals.get(link);
    targets.clear();
  }
  function disconnect() {
    generation++; observer?.disconnect(); observer = null;
    mutations?.disconnect(); mutations = null;
    clearTimeout(retryTimer); clearTimeout(expiryTimer);
  }
  function forget() { disconnect(); restore(); cohort = null; removeSaved(); }
  function current() {
    return active() && cohort?.exposedAt ? {experiment: EXPERIMENT, variant: cohort.variant} : {};
  }
  function loadCohort() {
    if (cohort) return true;
    try {
      const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (saved?.version === 1 && saved.experiment === EXPERIMENT && ['control', 'cost'].includes(saved.variant) && saved.expiresAt === END && (saved.exposedAt === null || (Number.isFinite(saved.exposedAt) && saved.exposedAt >= START && saved.exposedAt <= Date.now()))) {
        cohort = {version: 1, experiment: EXPERIMENT, variant: saved.variant, expiresAt: END, exposedAt: saved.exposedAt, clicked: saved.clicked === true};
      } else {
        const random = new Uint32Array(1); crypto.getRandomValues(random);
        cohort = {version: 1, experiment: EXPERIMENT, variant: random[0] % 2 ? 'cost' : 'control', expiresAt: END, exposedAt: null, clicked: false};
      }
      if (!save()) { cohort = null; return false; }
      return true;
    } catch (_) {
      // Malformed saved JSON is discarded. A blocked store keeps the ordinary CTA.
      removeSaved();
      try {
        const random = new Uint32Array(1); crypto.getRandomValues(random);
        cohort = {version: 1, experiment: EXPERIMENT, variant: random[0] % 2 ? 'cost' : 'control', expiresAt: END, exposedAt: null, clicked: false};
        if (save()) return true;
      } catch (_) {}
      cohort = null; return false;
    }
  }
  function emit(name) {
    if (!cohort || !active() || !transportReady()) return false;
    try {
      const bridge = window.CgauCommerceEvents, before = bridge.status().published;
      document.dispatchEvent(new CustomEvent('cgau:commerce', {detail: {name, experiment: EXPERIMENT, variant: cohort.variant}}));
      // Dispatch is synchronous. A missing, denied, limited or throwing transport is not an exposure.
      return Number.isFinite(before) && bridge.status().published === before + 1;
    } catch (_) { return false; }
  }
  function expose() {
    if (cohort?.exposedAt) return true;
    if (!emit('experiment_exposed')) return false;
    cohort.exposedAt = Date.now(); save();
    observer?.disconnect(); observer = null;
    clearTimeout(retryTimer);
    return true;
  }
  function visible(link) {
    for (let node = link; node instanceof Element; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    }
    const rect = link.getBoundingClientRect();
    if (!link.isConnected || rect.width <= 0 || rect.height <= 0) return false;
    const area = Math.max(0, Math.min(rect.right, innerWidth) - Math.max(rect.left, 0)) * Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0));
    return area / (rect.width * rect.height) >= .5;
  }
  function apply(scope = document) {
    if (!cohort || !active()) return;
    for (const link of targets) if (!link.isConnected) { observer?.unobserve(link); targets.delete(link); }
    const links = scope.matches?.(SELECTOR) ? [scope] : [...scope.querySelectorAll(SELECTOR)];
    for (const link of links) {
      if (!originals.has(link)) originals.set(link, link.textContent);
      link.textContent = cohort.variant === 'cost' ? 'Price a complete setup' : 'Build your setup';
      targets.add(link);
      if (!cohort.exposedAt) observer?.observe(link);
    }
  }
  function watch() {
    // Recreate registration together with its observer. No WeakSet survives a disconnected observer.
    disconnect();
    const token = generation;
    if (!cohort.exposedAt && typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(entries => {
        if (generation !== token || !active() || !cohort || cohort.exposedAt) return;
        const intersecting = entries.filter(entry => entry.isIntersecting && entry.intersectionRatio >= .5);
        if (intersecting.length && (!intersecting.some(entry => visible(entry.target)) || !expose())) retry();
      }, {threshold: .5});
    }
    const main = document.querySelector('main') || document.body;
    if (main && typeof MutationObserver === 'function') {
      mutations = new MutationObserver(records => {
        if (generation !== token || !active()) return;
        for (const record of records) for (const node of record.addedNodes) {
          if (node.nodeType === 1 && (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR))) apply(node);
        }
      });
      mutations.observe(main, {childList: true, subtree: true});
    }
    apply();
    expiryTimer = setTimeout(reconcile, Math.min(END - Date.now(), 2147483647));
  }
  function retry() {
    clearTimeout(retryTimer);
    if (stopped || suspended || document.hidden || !inWindow() || qa() || attempts >= 12) return;
    attempts++;
    retryTimer = setTimeout(reconcile, 500);
  }
  function reconcile() {
    clearTimeout(retryTimer);
    if (stopped || suspended || document.hidden) { disconnect(); return; }
    if (qa()) { disconnect(); restore(); cohort = null; return; }
    if (!inWindow()) { forget(); return; }
    if (!consent()) {
      forget();
      // Wait briefly for asynchronous script/API readiness, never poll an explicit denial.
      if (!window.CgauCommerceEvents || typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function' || typeof window.Shopify?.customerPrivacy?.marketingAllowed !== 'function') retry();
      return;
    }
    if (!transportReady()) { disconnect(); retry(); return; }
    if (!loadCohort()) return;
    watch();
    // Re-observing sends an initial intersection entry, including after resume/retry.
    // Do not replace its ancestor-clipping calculation with a viewport-only rectangle.
  }
  function consentChanged(event) {
    attempts = 0;
    if (event.detail?.analyticsAllowed === false || event.detail?.marketingAllowed === false) { forget(); return; }
    reconcile();
  }
  function onClick(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.defaultPrevented || !active()) return;
    const link = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
    if (!link || !targets.has(link) || !visible(link)) return;
    if (!cohort?.clicked && expose() && emit('experiment_clicked')) { cohort.clicked = true; save(); }
  }
  function visibilityChanged() { attempts = 0; reconcile(); }
  function pageHide() { suspended = true; disconnect(); }
  function pageShow() { suspended = false; attempts = 0; reconcile(); }
  function sectionLoaded(event) { if (active() && cohort) apply(event.target); else reconcile(); }
  function destroy() {
    stopped = true; disconnect(); restore(); cohort = null;
    document.removeEventListener('DOMContentLoaded', reconcile);
    document.removeEventListener('visitorConsentCollected', consentChanged);
    document.removeEventListener('visibilitychange', visibilityChanged);
    document.removeEventListener('shopify:section:load', sectionLoaded);
    document.removeEventListener('click', onClick);
    window.removeEventListener('pagehide', pageHide); window.removeEventListener('pageshow', pageShow);
    delete window.CgauCommerceExperiment;
  }
  window.CgauCommerceExperiment = Object.freeze({current, destroy});
  document.addEventListener('visitorConsentCollected', consentChanged);
  document.addEventListener('visibilitychange', visibilityChanged);
  document.addEventListener('shopify:section:load', sectionLoaded);
  document.addEventListener('click', onClick);
  window.addEventListener('pagehide', pageHide); window.addEventListener('pageshow', pageShow);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reconcile, {once: true});
  else reconcile();
})();
